/**
 * Breadcrumb Context — provides a reactive store for breadcrumb titles.
 *
 * Replaces the unreliable window.__coreflow_*_title globals with a proper
 * React context that triggers re-renders when titles are updated.
 *
 * Page components call setBoardTitle/setLibraryTitle/setPageTitle to update
 * the breadcrumb display. The Breadcrumbs component subscribes via useBreadcrumb()
 * and re-renders automatically when any title changes.
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BreadcrumbState {
  readonly boardTitle: string | null;
  readonly libraryTitle: string | null;
  readonly pageTitle: string | null;
}

interface BreadcrumbContextValue {
  readonly state: BreadcrumbState;
  readonly setBoardTitle: (title: string | null) => void;
  readonly setLibraryTitle: (title: string | null) => void;
  readonly setPageTitle: (title: string | null) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function BreadcrumbProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [boardTitle, setBoardTitleState] = useState<string | null>(null);
  const [libraryTitle, setLibraryTitleState] = useState<string | null>(null);
  const [pageTitle, setPageTitleState] = useState<string | null>(null);

  const setBoardTitle = useCallback((title: string | null) => {
    setBoardTitleState(title);
    // Keep window global in sync for any legacy consumers
    (window as unknown as Record<string, unknown>).__coreflow_board_title = title ?? undefined;
  }, []);

  const setLibraryTitle = useCallback((title: string | null) => {
    setLibraryTitleState(title);
    (window as unknown as Record<string, unknown>).__coreflow_library_title = title ?? undefined;
  }, []);

  const setPageTitle = useCallback((title: string | null) => {
    setPageTitleState(title);
    (window as unknown as Record<string, unknown>).__coreflow_page_title = title ?? undefined;
  }, []);

  const value = useMemo<BreadcrumbContextValue>(() => ({
    state: { boardTitle, libraryTitle, pageTitle },
    setBoardTitle,
    setLibraryTitle,
    setPageTitle,
  }), [boardTitle, libraryTitle, pageTitle, setBoardTitle, setLibraryTitle, setPageTitle]);

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Hook to access breadcrumb state and setters.
 * Must be used within a BreadcrumbProvider.
 */
export function useBreadcrumb(): BreadcrumbContextValue {
  const ctx = useContext(BreadcrumbContext);
  if (!ctx) {
    throw new Error('useBreadcrumb must be used within a BreadcrumbProvider');
  }
  return ctx;
}
